import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, User, MessageCircle, Calendar, AlertCircle } from 'lucide-react';
import useCommunityStore from '../../store/communityStore';
import useAuthStore from '../../store/authStore';
import PostCard from '../../components/community/PostCard';
import CommentsSection from '../../components/community/CommentsSection';
import { toast } from 'react-hot-toast';

const PostPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [post, setPost] = useState(null);
  
  const { getPost, postComments } = useCommunityStore();
  const { user } = useAuthStore();

  useEffect(() => {
    if (postId) {
      loadPost();
    }
  }, [postId]);

  useEffect(() => {
    if (post && location.state?.commentId) {
      const scrollToComment = () => {
        setTimeout(() => {
          const commentElement = document.getElementById(`comment-${location.state.commentId}`);
          
          if (commentElement) {
            commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Add highlight effect with neumorphic style
            commentElement.classList.add(
              'bg-yellow-50',
              'shadow-neomorph-inset',
              'border',
              'border-yellow-200',
              'transition-all',
              'duration-500'
            );
            
            setTimeout(() => {
              commentElement.classList.remove(
                'bg-yellow-50',
                'shadow-neomorph-inset',
                'border',
                'border-yellow-200'
              );
            }, 2000);
          } else {
            console.log('Comment not found in current view, might be paginated');
            toast.success('Scroll down to see the comment', {
              icon: '👇',
              duration: 3000,
            });
          }
        }, 800);
      };
      
      scrollToComment();
    }
  }, [post, postComments, location.state]);

  const loadPost = async () => {
    setIsLoading(true);
    try {
      const response = await getPost(postId);
      setPost(response);
      
      if (!response) {
        throw new Error('Post not found');
      }
    } catch (error) {
      console.error('Failed to load post:', error);
      toast.error(error.message || 'Post not found or unavailable');
      navigate('/community');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (location.state?.fromCommunity) {
      navigate('/community', { state: { tab: 'feed' } });
    } else {
      navigate('/community');
    }
  };

  const handlePostDeleted = () => {
    toast.success('Post deleted successfully');
    handleBack();
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-4 sm:py-6 md:py-8 px-3 sm:px-4 pb-20 sm:pb-6">
        {/* Back Button Skeleton */}
        <div className="animate-pulse mb-4 sm:mb-6">
          <div className="flex items-center">
            <div className="h-4 w-4 sm:h-5 sm:w-5 bg-gray-200 rounded mr-1.5 sm:mr-2"></div>
            <div className="h-3 sm:h-4 bg-gray-200 rounded w-20 sm:w-24"></div>
          </div>
        </div>
        
        {/* Post Card Skeleton with neumorphic style */}
        <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30 animate-pulse">
          {/* User Info Skeleton */}
          <div className="flex items-start space-x-2 sm:space-x-3 mb-4 sm:mb-6">
            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300"></div>
            <div className="space-y-1.5 sm:space-y-2 flex-1">
              <div className="h-3 sm:h-4 bg-gray-200 rounded w-24 sm:w-32"></div>
              <div className="h-2 sm:h-3 bg-gray-200 rounded w-20 sm:w-24"></div>
            </div>
          </div>
          
          {/* Content Skeleton */}
          <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
            <div className="h-3 sm:h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-3 sm:h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-3 sm:h-4 bg-gray-200 rounded w-4/6"></div>
            <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/6"></div>
          </div>
          
          {/* Media Skeleton */}
          <div className="h-48 sm:h-64 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-neomorph-inset mb-4 sm:mb-6"></div>
          
          {/* Actions Skeleton */}
          <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-primary-100/50">
            <div className="flex space-x-4 sm:space-x-6">
              <div className="h-3 sm:h-4 bg-gray-200 rounded w-12 sm:w-16"></div>
              <div className="h-3 sm:h-4 bg-gray-200 rounded w-12 sm:w-16"></div>
            </div>
            <div className="h-3 sm:h-4 bg-gray-200 rounded w-12 sm:w-16"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto py-8 sm:py-12 px-3 sm:px-4 pb-20 sm:pb-12">
        <div className="bg-white rounded-xl shadow-neomorph p-6 sm:p-8 text-center border border-primary-100/30">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-50 shadow-neomorph-inset mb-3 sm:mb-4">
            <AlertCircle className="h-8 w-8 sm:h-10 sm:w-10 text-red-400" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Post not found</h2>
          <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
            The post you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={handleBack}
            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-sm"
          >
            Back to FastConnect
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-3 sm:py-4 md:py-6 lg:py-8 px-3 sm:px-4 pb-20 sm:pb-6">
      {/* Back Button with neumorphic style */}
      <button
        onClick={handleBack}
        className="group flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all mb-4 sm:mb-6 border border-primary-100/30"
      >
        <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-gray-500 group-hover:-translate-x-1 transition-transform" />
        <span className="text-xs sm:text-sm font-medium text-gray-700">Back to FastConnect</span>
      </button>

      {/* Post */}
      <div className="mb-4 sm:mb-6">
        <PostCard post={post} onPostDeleted={handlePostDeleted} />
      </div>

      {/* Comments Section */}
      <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30">
        <div className="flex items-center mb-3 sm:mb-6">
          <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2 sm:mr-3" />
          <div>
            <h2 className="text-sm sm:text-lg font-semibold text-gray-800">Comments</h2>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 flex items-center">
              <MessageCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
              Join the conversation about this post
            </p>
          </div>
        </div>
        
        <CommentsSection postId={postId} />
      </div>
      
      {/* Debug Info (only in development) */}
      {process.env.NODE_ENV === 'development' && location.state?.commentId && (
        <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-blue-50 rounded-xl shadow-neomorph-inset border border-blue-200">
          <div className="flex items-start">
            <div className="p-1 sm:p-1.5 bg-blue-100 rounded-lg mr-2 sm:mr-3 flex-shrink-0">
              <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-blue-800 mb-0.5 sm:mb-1">Debug Information</p>
              <p className="text-[10px] sm:text-xs text-blue-700 break-words">
                Looking for comment ID: {location.state.commentId}
              </p>
              <p className="text-[10px] sm:text-xs text-blue-700 mt-1">
                Total comments loaded: {postComments.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostPage;